import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, NavLink } from 'react-router-dom';
import {
  clients as clientsApi, intakes as intakesApi, fieldWorks as worksApi, gps as gpsApi,
  type FieldWorkFilter, type FieldWorkKindCode, type FieldWorkMapPoint,
  type FieldWorkMeta, type FieldWorkStatusCode,
  type GpsIntegrationStatus, type GpsVehiclePosition,
} from '@/api/endpoints';
import { hasPermission, type Client, type Intake } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { OverviewMap, type OverviewMarker } from '@/components/OverviewMap';

/**
 * Mapa przeglądu — JEDNA mapa całej firmy: ujęcia, klienci i stan prac terenowych.
 *
 * Do sierpnia 2026 były to dwie strony (/mapa-przeglad i /prace/mapa) rysujące tę samą pinezkę
 * klienta w dwóch różnych miejscach: przegląd liczył średnią współrzędnych ujęć, mapa prac brała
 * ręcznie wpisany punkt klienta. Tutaj obowiązuje jedna reguła: własny punkt klienta wygrywa,
 * a średnia ujęć jest tylko pozycją ZAPASOWĄ i jest oznaczona jako przybliżona.
 *
 * Warstwy są niezależne i każda degraduje się osobno:
 *  - brak uprawnienia → warstwy nie ma (nie wołamy API, nie ma przełącznika),
 *  - błąd jednej warstwy nie wywraca strony (dawniej Promise.all robił z 403 ekran błędu).
 *
 * Kolory stanu robót i regułę pierwszeństwa (W toku › Zlecone › Oczekiwane › Zakończone) liczy
 * SERWER i przysyła razem z punktami — front ich nie odtwarza, inaczej legenda i pinezki miałyby
 * dwie niezależne definicje tego samego.
 *
 * Czwarta warstwa — POJAZDY (V159) — jest inna niż pozostałe trzy: nie opisuje stanu rejestru, tylko
 * to, co dzieje się w tej chwili, i sama się odświeża. Kolory stanu lokalizatora również liczy serwer.
 */

/** Kolor pinezki klienta, gdy nie niesie stanu robót. */
const CLIENT_BLUE = '#2563eb';
/**
 * Turkus ujęcia (water-600). NIE domyślny pomarańcz OverviewMap: #ea580c niesie na tej mapie stan
 * „W toku" wśród prac, a dwa różne znaczenia jednego koloru na jednej mapie to zgadywanka.
 */
const INTAKE_CYAN = '#0891b2';

/** Klient na mapie — złożony z trzech źródeł: listy klientów, punktów prac i ujęć. */
interface ClientPin {
  id: number;
  name: string;
  city: string | null;
  locationNote: string | null;
  lat: number;
  lng: number;
  /** true = pozycja policzona ze średniej ujęć, bo klient nie ma własnej pinezki. */
  approx: boolean;
  /** Ile geolokalizowanych ujęć ma ten klient (badge, gdy warstwa prac wyłączona). */
  intakesOnMap: number;
  /** Punkt z /api/field-works/map — null gdy brak uprawnienia albo klienta nie ma w odpowiedzi. */
  works: FieldWorkMapPoint | null;
}

/** Klient, którego nie da się narysować: bez własnego punktu i bez geolokalizowanych ujęć. */
interface ClientOffMap {
  id: number;
  name: string;
  city: string | null;
}

export function MapOverview() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canIntakes = hasPermission(user, 'INTAKE_VIEW');
  const canClients = hasPermission(user, 'CLIENT_VIEW');
  const canWorks = hasPermission(user, 'FIELDWORK_VIEW');
  const canFleet = hasPermission(user, 'FLEET_VIEW');
  // Pozycje pojazdów to osobne uprawnienie — /api/gps/positions wymaga FLEET_TRACK.
  const canTrack = hasPermission(user, 'FLEET_TRACK');

  const [clients, setClients] = useState<Client[]>([]);
  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [points, setPoints] = useState<FieldWorkMapPoint[] | null>(null);
  const [meta, setMeta] = useState<FieldWorkMeta | null>(null);
  /** Konfiguracja integracji GPS; null = jeszcze nie wiadomo, bo pytanie jest w locie. */
  const [gpsCfg, setGpsCfg] = useState<GpsIntegrationStatus | null>(null);
  const [vehicles, setVehicles] = useState<GpsVehiclePosition[]>([]);
  /** Czas ostatniego UDANEGO odczytu pozycji — bez tego nie widać, że warstwa zamarła. */
  const [gpsFetchedAt, setGpsFetchedAt] = useState<Date | null>(null);
  /** Błędy per warstwa — jedna niedostępna warstwa nie może zabrać pozostałych. */
  const [layerErrs, setLayerErrs] = useState<Record<string, string>>({});
  const [baseLoading, setBaseLoading] = useState(canIntakes || canClients);
  const [worksLoading, setWorksLoading] = useState(canWorks);

  // Warstwy: domyślnie włączone wszystko, do czego użytkownik ma prawo.
  const [showIntakes, setShowIntakes] = useState(canIntakes);
  const [showClients, setShowClients] = useState(true);
  const [showWorks, setShowWorks] = useState(canWorks);
  const [showVehicles, setShowVehicles] = useState(canFleet);

  // Filtry warstwy prac — ten sam zestaw kryteriów co rejestr i podsumowanie.
  const [statuses, setStatuses] = useState<FieldWorkStatusCode[]>([]);
  const [kind, setKind] = useState<FieldWorkKindCode | ''>('');
  const [executedFrom, setExecutedFrom] = useState('');
  const [executedTo, setExecutedTo] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);

  // Deep-link „pokaż tego klienta" (z karty klienta). Trzymany w adresie, nie w stanie —
  // odświeżenie strony i link wysłany koledze trafiają w to samo miejsce.
  const [params, setParams] = useSearchParams();
  const focusClientId = params.get('clientId');

  const noteErr = (layer: string, e: unknown) =>
    setLayerErrs(prev => ({ ...prev, [layer]: e instanceof Error ? e.message : String(e) }));

  useEffect(() => {
    if (!canIntakes && !canClients) { setBaseLoading(false); return; }
    setBaseLoading(true);
    const jobs: Promise<unknown>[] = [];
    if (canClients) jobs.push(clientsApi.list().then(setClients).catch(e => noteErr('Klienci', e)));
    if (canIntakes) jobs.push(intakesApi.list().then(setIntakes).catch(e => noteErr('Ujęcia', e)));
    // allSettled, nie all: 403 na jednej warstwie ma zostawić pozostałe na mapie.
    Promise.allSettled(jobs).finally(() => setBaseLoading(false));
  }, [canIntakes, canClients]);

  useEffect(() => {
    if (!canWorks) return;
    worksApi.meta().then(setMeta).catch(e => noteErr('Prace (słownik)', e));
  }, [canWorks]);

  const filter: FieldWorkFilter = useMemo(() => ({
    status: statuses.length ? statuses : undefined,
    kind: kind ? [kind] : undefined,
    executedFrom: executedFrom || undefined,
    executedTo: executedTo || undefined,
  }), [statuses, kind, executedFrom, executedTo]);

  useEffect(() => {
    if (!canWorks) { setPoints(null); return; }
    setWorksLoading(true);
    // Strażnik kolejności: przy szybkim klikaniu filtrów wolniejsza STARSZA odpowiedź potrafi wrócić
    // po nowszej i pokazać dane, których filtry już nie opisują.
    let aktualne = true;
    worksApi.map(filter)
      .then(ps => {
        if (!aktualne) return;
        setPoints(ps);
        setLayerErrs(prev => { const rest = { ...prev }; delete rest.Prace; return rest; });
      })
      .catch(e => { if (aktualne) { setPoints([]); noteErr('Prace', e); } })
      .finally(() => { if (aktualne) setWorksLoading(false); });
    return () => { aktualne = false; };
  }, [filter, canWorks]);

  // ── Warstwa POJAZDY (GPS) ───────────────────────────────────────────────────
  // Najpierw pytamy, czy integracja w ogóle działa i jak często odświeżać. Bez tego kroku każda
  // otwarta mapa dobijałaby się cyklicznie o pozycje także tam, gdzie dostawcy GPS nie ma wcale.
  useEffect(() => {
    if (!canFleet) { setGpsCfg(null); return; }
    let aktualne = true;
    gpsApi.status()
      .then(st => { if (aktualne) setGpsCfg(st); })
      .catch(e => { if (aktualne) { setGpsCfg(null); noteErr('Pojazdy (GPS)', e); } });
    return () => { aktualne = false; };
  }, [canFleet]);

  const gpsAktywne = canTrack && showVehicles && (gpsCfg?.enabled ?? false);

  /**
   * Cykliczny odczyt pozycji. Trzy rzeczy, bez których to by szkodziło:
   *  - odpytujemy TYLKO przy włączonej warstwie — wyłączona warstwa nie może generować ruchu,
   *  - w nieaktywnej karcie przeglądarki pauzujemy (`visibilitychange`): zapomniana zakładka
   *    potrafi wisieć tygodniami i sama z siebie odpytywałaby dostawcę bez końca,
   *  - błąd NIE kasuje ostatnich pozycji — zniknięcie floty z mapy przy jednym nieudanym
   *    odpytaniu wyglądałoby jak „auta stoją w bazie", a nie jak chwilowy brak łączności.
   */
  useEffect(() => {
    if (!gpsAktywne) return;
    let aktualne = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    // Strażnik kolejności, jak przy warstwie prac: przy wolnej sieci starsza odpowiedź potrafi wrócić
    // po nowszej i cofnąć flotę do pozycji sprzed minuty — ze świeżym znacznikiem czasu odczytu,
    // więc bez śladu, że pokazujemy stan starszy niż poprzedni.
    let numerZadania = 0;
    let ostatniPokazany = 0;

    const pobierz = () => {
      if (document.hidden) return;
      const moj = ++numerZadania;
      gpsApi.positions()
        .then(ps => {
          if (!aktualne || moj < ostatniPokazany) return;
          ostatniPokazany = moj;
          setVehicles(ps);
          setGpsFetchedAt(new Date());
          setLayerErrs(prev => { const rest = { ...prev }; delete rest['Pojazdy (GPS)']; return rest; });
        })
        .catch(e => { if (aktualne) noteErr('Pojazdy (GPS)', e); });
    };

    pobierz();
    const co = gpsCfg?.refreshSeconds ?? 0;
    if (co > 0) timer = setInterval(pobierz, co * 1000);
    // Powrót do karty ma pokazać stan NA TERAZ, a nie ten sprzed przerwy — stąd odczyt od razu.
    const naWidocznosc = () => { if (!document.hidden) pobierz(); };
    document.addEventListener('visibilitychange', naWidocznosc);

    return () => {
      aktualne = false;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', naWidocznosc);
    };
  }, [gpsAktywne, gpsCfg?.refreshSeconds]);

  // Uprawnienia potrafią zmienić się w trakcie sesji (AuthContext odświeża je bez przeładowania).
  // Bez tego warstwa zabrana użytkownikowi zostawałaby na mapie ze starymi danymi.
  useEffect(() => { setShowWorks(canWorks); }, [canWorks]);
  useEffect(() => { if (!canFleet) setVehicles([]); setShowVehicles(canFleet); }, [canFleet]);
  useEffect(() => { if (!canIntakes) setIntakes([]); setShowIntakes(canIntakes); }, [canIntakes]);

  const intakesWithCoords = useMemo(
    () => intakes.filter(i => i.latitude != null && i.longitude != null),
    [intakes]);

  /**
   * Rejestr klientów sklejony z trzech źródeł. Każde może być niedostępne (uprawnienia), więc
   * żadne nie jest wymagane: nazwa i miasto przychodzą z tego źródła, które akurat je zna.
   */
  const { clientPins, clientsOffMap } = useMemo(() => {
    interface Acc {
      id: number; name: string; city: string | null; note: string | null;
      lat: number | null; lng: number | null;
      sumLat: number; sumLng: number; intakes: number;
      works: FieldWorkMapPoint | null;
    }
    const acc = new Map<number, Acc>();
    const touch = (id: number, name: string, city: string | null, note: string | null): Acc => {
      let e = acc.get(id);
      if (!e) {
        e = { id, name, city, note, lat: null, lng: null, sumLat: 0, sumLng: 0, intakes: 0, works: null };
        acc.set(id, e);
      }
      if (!e.name && name) e.name = name;
      if (!e.city && city) e.city = city;
      if (!e.note && note) e.note = note;
      return e;
    };
    const own = (e: Acc, lat: unknown, lng: unknown) => {
      if (e.lat != null || lat == null || lng == null) return;
      e.lat = Number(lat); e.lng = Number(lng);
    };

    for (const c of clients) {
      own(touch(c.id, c.name, c.city ?? null, c.locationNote ?? null), c.latitude, c.longitude);
    }
    // Punkty prac niosą własny punkt klienta także wtedy, gdy nie mamy CLIENT_VIEW.
    for (const p of points ?? []) {
      const e = touch(p.clientId, p.clientName, p.city, p.locationNote);
      own(e, p.latitude, p.longitude);
      e.works = p;
    }
    for (const i of intakesWithCoords) {
      if (!i.client) continue;
      const e = touch(i.client.id, i.client.name, i.client.city ?? null, i.client.locationNote ?? null);
      own(e, i.client.latitude, i.client.longitude);
      e.intakes += 1;
      e.sumLat += Number(i.latitude); e.sumLng += Number(i.longitude);
    }
    // Ujęcia bez uprawnienia do listy klientów też mają klienta zagnieżdżonego — ale klienci
    // znani wyłącznie z listy /api/clients (bez ujęć i bez prac) również muszą tu być.

    const pins: ClientPin[] = [];
    const off: ClientOffMap[] = [];
    for (const e of acc.values()) {
      const approx = e.lat == null && e.intakes > 0;
      const lat = e.lat != null ? e.lat : (e.intakes > 0 ? e.sumLat / e.intakes : null);
      const lng = e.lng != null ? e.lng : (e.intakes > 0 ? e.sumLng / e.intakes : null);
      if (lat == null || lng == null) {
        off.push({ id: e.id, name: e.name, city: e.city });
        continue;
      }
      pins.push({
        id: e.id, name: e.name, city: e.city, locationNote: e.note,
        lat, lng, approx, intakesOnMap: e.intakes, works: e.works,
      });
    }
    off.sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    return { clientPins: pins, clientsOffMap: off };
  }, [clients, points, intakesWithCoords]);

  /**
   * „Ukryj klientów bez prac" — filtr WARSTWY PRAC, dokładnie taki jak na dawnej mapie prac:
   * po kliknięciu statusu na mapie mają zostać sami klienci z pasującymi robotami. Gdyby przepuszczał
   * jeszcze klientów „bo mają ujęcia", filtr statusu przestałby zawężać zbiór pinezek i zawężałby
   * już tylko liczniki — mapa i liczby odpowiadałyby na to samo pytanie inaczej.
   *
   * Przeglądowa połowa mapy nic na tym nie traci: ujęcia to osobna warstwa i są widoczne niezależnie,
   * a klienci wracają po odznaczeniu tego pola albo po wyłączeniu warstwy prac.
   *
   * Przy wyłączonej warstwie (albo bez uprawnienia do prac) filtr NIE działa — chowanie punktów na
   * podstawie danych, których nie widać, kończyło się znikaniem klientów bez możliwości ich odzyskania.
   * Klient wskazany deep-linkiem jest z filtra wyjęty — „Pokaż na mapie" ma pokazywać.
   */
  const filtrPracAktywny = canWorks && showWorks && hideEmpty;
  const visiblePins = useMemo(
    () => clientPins.filter(c =>
      !filtrPracAktywny
      || String(c.id) === focusClientId
      || (c.works?.worksTotal ?? 0) > 0),
    [clientPins, filtrPracAktywny, focusClientId]);

  const markers = useMemo<OverviewMarker[]>(() => {
    const out: OverviewMarker[] = [];
    // Klienci PRZED ujęciami: markery są rysowane w kolejności dodania, a pin klienta (32 px) w tym
    // samym punkcie co jego jedyne ujęcie (22 px) przykryłby je w całości i odebrał klikalność.
    if (showClients) {
      for (const c of visiblePins) {
        const colored = showWorks && c.works != null;
        out.push({
          id: `c-${c.id}`,
          type: 'client',
          lat: c.lat,
          lng: c.lng,
          label: c.name,
          color: colored ? c.works!.color : CLIENT_BLUE,
          badge: colored ? (c.works!.worksTotal || undefined) : (c.intakesOnMap || undefined),
          // Praca po terminie to jedyny stan, który musi krzyczeć z samej pinezki.
          badgeColor: colored && c.works!.worksOverdue > 0 ? '#dc2626' : undefined,
          popupHtml: clientPopup(c, { showWorks, canClients, canWorks }),
        });
      }
    }
    if (showIntakes) {
      for (const i of intakesWithCoords) {
        out.push({
          id: `i-${i.id}`,
          type: 'intake',
          lat: Number(i.latitude),
          lng: Number(i.longitude),
          label: i.name,
          color: INTAKE_CYAN,
          popupHtml: intakePopup(i, canClients),
        });
      }
    }
    // Pojazdy NA KOŃCU: markery rysują się w kolejności dodania, a warstwa „co jest teraz" musi
    // być klikalna także tam, gdzie auto stoi dokładnie na pinezce klienta, do którego przyjechało.
    if (gpsAktywne) {
      for (const v of vehicles) {
        out.push({
          id: `v-${v.deviceId}`,
          type: 'vehicle',
          lat: v.latitude,
          lng: v.longitude,
          label: v.displayName,
          color: v.color,
          // Kurs pokazujemy tylko przy realnym ruchu. Stojące auto raportuje ostatni kurs sprzed
          // zatrzymania — strzałka wskazywałaby wtedy kierunek, w którym nic już nie jedzie.
          heading: v.status === 'DRIVING' ? v.headingDeg : undefined,
          dimmed: v.stale,
          popupHtml: vehiclePopup(v, canFleet),
        });
      }
    }
    return out;
  }, [showIntakes, showClients, showWorks, intakesWithCoords, visiblePins, canClients, canWorks,
      gpsAktywne, vehicles, canFleet]);

  const totals = useMemo(() => {
    const t = { inProgress: 0, ordered: 0, pending: 0, done: 0, overdue: 0, total: 0 };
    for (const c of visiblePins) {
      const w = c.works;
      if (!w) continue;
      t.inProgress += w.worksInProgress; t.ordered += w.worksOrdered; t.pending += w.worksPending;
      t.done += w.worksDone; t.overdue += w.worksOverdue; t.total += w.worksTotal;
    }
    return t;
  }, [visiblePins]);

  /** Rozbicie floty na stany — do panelu i legendy. Liczone z tego, co NARYSOWANE. */
  const gpsTotals = useMemo(() => {
    const t = { driving: 0, idling: 0, parked: 0, stale: 0, unmatched: 0, total: vehicles.length };
    for (const v of vehicles) {
      if (v.status === 'DRIVING') t.driving++;
      else if (v.status === 'IDLING') t.idling++;
      else if (v.status === 'STALE') t.stale++;
      else t.parked++;
      if (v.vehicleId == null) t.unmatched++;
    }
    return t;
  }, [vehicles]);

  const intakesWithoutCoords = intakes.length - intakesWithCoords.length;
  const approxPins = visiblePins.filter(c => c.approx).length;
  // Prawdą o „czy widać" jest zbiór narysowanych markerów, nie zbiór policzonych pinezek:
  // klient bez współrzędnych i klient schowany przez wyłączoną warstwę to dwa różne komunikaty.
  const focusPin = focusClientId ? clientPins.find(c => String(c.id) === focusClientId) : undefined;
  const focusNarysowany = focusClientId
    ? markers.some(m => m.id === `c-${focusClientId}`)
    : false;
  const busy = baseLoading || worksLoading;
  const errList = Object.entries(layerErrs);

  const toggleStatus = (s: FieldWorkStatusCode) =>
    setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const clearFocus = () => {
    const p = new URLSearchParams(params);
    p.delete('clientId');
    setParams(p, { replace: true });
  };

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1>Mapa przeglądu</h1>
          <div className="text-ink-400 text-[13px]">
            Ujęcia, klienci i stan prac terenowych na jednym widoku
          </div>
        </div>
        <div className="flex items-center gap-4 text-[13px] flex-wrap">
          {canIntakes && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showIntakes} onChange={e => setShowIntakes(e.target.checked)}/>
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: INTAKE_CYAN }}/>
              Ujęcia ({intakesWithCoords.length})
            </label>
          )}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showClients} onChange={e => setShowClients(e.target.checked)}/>
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: CLIENT_BLUE }}/>
            Klienci ({visiblePins.length})
          </label>
          {canWorks && (
            <label className={'flex items-center gap-1.5 ' + (showClients ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed')}
                   title={showClients
                     ? 'Koloruje pinezki klientów stanem ich robót i włącza filtry prac'
                     : 'Stan prac niosą pinezki klientów — włącz warstwę „Klienci"'}>
              <input type="checkbox" checked={showWorks} disabled={!showClients}
                     onChange={e => setShowWorks(e.target.checked)}/>
              <Icon name="drill" className="w-3.5 h-3.5 text-ink-500"/>
              Prace ({totals.total})
            </label>
          )}
          {canFleet && gpsCfg?.enabled && (
            <label className="flex items-center gap-1.5 cursor-pointer"
                   title="Ostatnie znane pozycje pojazdów z lokalizatorów GPS. Warstwa odświeża się sama.">
              <input type="checkbox" checked={showVehicles} onChange={e => setShowVehicles(e.target.checked)}/>
              <Icon name="truck" className="w-3.5 h-3.5 text-ink-500"/>
              {/* Licznik TYLKO przy włączonej warstwie: wyłączona warstwa nie odpytuje dostawcy,
                  więc „Pojazdy (0)" twierdziłoby, że floty nie ma, zamiast że jej nie sprawdzamy. */}
              Pojazdy{gpsAktywne ? ` (${vehicles.length})` : ''}
            </label>
          )}
          {canWorks && (
            <NavLink to="/prace" className="btn btn-secondary btn-sm">
              <Icon name="layout" className="w-3.5 h-3.5"/> Rejestr prac
            </NavLink>
          )}
        </div>
      </header>

      {errList.length > 0 && (
        <div className="text-amber-800 p-3 bg-amber-50 border border-amber-200 rounded mb-3 text-[13px]">
          {errList.map(([layer, msg]) => (
            <div key={layer}>Warstwa „{layer}" niedostępna: {msg}</div>
          ))}
        </div>
      )}

      {focusClientId && (
        <div className="flex items-center gap-2 mb-3 text-[13px]">
          <span className="pill pill-water">
            Na mapie: {focusPin?.name ?? `klient #${focusClientId}`}
          </span>
          <button onClick={clearFocus} className="text-ink-500 hover:text-ink-800 text-[12px]">
            pokaż wszystkich
          </button>
          {!focusNarysowany && (
            <span className="text-amber-700 text-[12px]">
              {focusPin == null
                ? '— tego klienta nie ma na mapie: nie ma ani własnych współrzędnych, ani geolokalizowanego ujęcia'
                : '— pinezka ukryta: włącz warstwę „Klienci"'}
            </span>
          )}
        </div>
      )}

      {/* Cała karta należy do warstwy prac — bez FIELDWORK_VIEW nie ma czego filtrować, a pusty pasek
          z komunikatem tylko zabierał wysokość mapie. */}
      {canWorks && (
      <div className="card mb-4">
        <div className="card-head flex-wrap gap-2">
          {/* Filtry dotyczą WYŁĄCZNIE warstwy prac — przy wyłączonej warstwie zostają widoczne,
              ale nieaktywne (żeby „gdzie się podziały filtry?" nie kończyło się szukaniem). */}
          <div className={'flex gap-1 flex-wrap' + (showWorks ? '' : ' opacity-50')}>
            {(meta?.statuses ?? []).map(s => (
              <button key={s.id} onClick={() => toggleStatus(s.id)} disabled={!showWorks}
                      className={'btn btn-xs ' + (statuses.includes(s.id) ? 'btn-primary' : 'btn-ghost')}>
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: s.mapColor }}/>
                {s.name}
              </button>
            ))}
          </div>
          <div className={'w-[190px]' + (showWorks ? '' : ' opacity-50 pointer-events-none')}>
            <SearchableSelect value={kind} onChange={v => setKind(v as FieldWorkKindCode | '')}>
              <option value="">Rodzaj — wszystkie</option>
              {(meta?.kinds ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </SearchableSelect>
          </div>
          <label className={'flex items-center gap-1.5 text-[12px]' + (showWorks ? '' : ' opacity-50')}>
            <span className="text-ink-500">Wykonane od</span>
            <input type="date" value={executedFrom} onChange={e => setExecutedFrom(e.target.value)}
                   disabled={!showWorks} className="input !py-1 !text-[12px] w-[135px]"/>
          </label>
          <label className={'flex items-center gap-1.5 text-[12px]' + (showWorks ? '' : ' opacity-50')}>
            <span className="text-ink-500">do</span>
            <input type="date" value={executedTo} onChange={e => setExecutedTo(e.target.value)}
                   disabled={!showWorks} className="input !py-1 !text-[12px] w-[135px]"/>
          </label>
          <label className={'flex items-center gap-1.5 text-[12px] ml-auto '
                            + (showWorks ? 'cursor-pointer' : 'opacity-50')}
                 title="Zostawia na mapie tylko klientów z pracami pasującymi do filtrów. Pinezki ujęć są osobną warstwą i zostają.">
            <input type="checkbox" checked={hideEmpty} disabled={!showWorks}
                   onChange={e => setHideEmpty(e.target.checked)}/>
            <span>Ukryj klientów bez prac</span>
          </label>
          {busy && <span className="text-[11px] text-ink-400">wczytywanie…</span>}
        </div>
      </div>
      )}

      <div className="grid grid-cols-[1fr_320px] gap-4">
        <div className="card overflow-hidden">
          {/* Mapa zostaje ZAMONTOWANA także przy pustym zbiorze punktów: odmontowanie niszczy
              instancję MapLibre razem z pozycją i zoomem, więc wyłączenie ostatniej warstwy
              kasowałoby dojazd do powiatu, a jej ponowne włączenie startowało od środka Polski. */}
          <div className="card-body !p-0 relative">
            <OverviewMap
              markers={markers}
              height="74vh"
              focusId={focusClientId ? `c-${focusClientId}` : null}
              onNavigate={to => nav(to)}
            />
            {markers.length === 0 && (
              <div className="absolute inset-0 grid place-items-center bg-white/85 p-8 text-center
                              text-ink-400 text-[13px] pointer-events-none">
                {busy
                  ? 'Ładowanie mapy…'
                  : 'Brak punktów do wyświetlenia. Włącz warstwę, poluzuj filtry (także „Ukryj klientów bez prac") albo uzupełnij współrzędne klientów i ujęć.'}
              </div>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-3">
          <div className="card">
            <div className="card-head"><div className="card-title">Legenda</div></div>
            <div className="card-body text-[12px] text-ink-700 grid gap-2">
              {canIntakes && (
                <div className="flex items-center gap-2">
                  <Icon name="droplet" className="w-4 h-4" style={{ color: INTAKE_CYAN }}/>
                  Turkusowy pin (mniejszy) = ujęcie.
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icon name="users" className="w-4 h-4" style={{ color: CLIENT_BLUE }}/>
                Niebieski pin (większy) = klient; plakietka ={' '}
                {showWorks && canWorks ? 'liczba prac' : 'liczba ujęć'}.
              </div>
              {showWorks && canWorks && (
                <>
                  <div className="border-t border-ink-100 pt-2 mt-1 text-ink-500 text-[11px]">
                    Z włączoną warstwą prac pin klienta niesie STAN robót, a plakietka — ich liczbę:
                  </div>
                  {(meta?.statuses ?? []).map(s => (
                    <div key={s.id} className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full" style={{ background: s.mapColor }}/>
                      <span>{s.name}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full"
                          style={{ background: meta?.noWorksColor ?? '#9ca3af' }}/>
                    <span>Brak prac</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: CLIENT_BLUE }}/>
                    <span>Klient spoza rejestru prac (pozycja z ujęć)</span>
                  </div>
                  <div className="text-[11px] text-ink-500">
                    Kolor = najważniejszy status wśród prac klienta:
                    <strong> W toku</strong> wygrywa nad <strong>Zlecone</strong>, te nad
                    <strong> Oczekiwane</strong>, a te nad <strong>Zakończone</strong>.
                    <span className="text-red-600 font-semibold"> Czerwona</span> plakietka —
                    są prace po terminie umowy.
                  </div>
                </>
              )}
              {gpsAktywne && (
                <>
                  <div className="border-t border-ink-100 pt-2 mt-1 text-ink-500 text-[11px]">
                    Pojazdy to kółka (nie pinezki) — stoją środkiem dokładnie na pozycji:
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#16a34a' }}/>
                    <span>W ruchu — strzałka pokazuje kurs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#f59e0b' }}/>
                    <span>Postój z zapłonem</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#64748b' }}/>
                    <span>Postój</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full opacity-55" style={{ background: '#9ca3af' }}/>
                    <span>Sygnał nieaktualny (ponad {gpsCfg?.staleMinutes ?? 30} min bez pomiaru)</span>
                  </div>
                </>
              )}
              <div className="text-[10px] text-ink-500 border-t border-ink-100 pt-2 mt-1">
                Pin klienta stoi w jego własnym punkcie (karta klienta → „Współrzędne klienta").
                {canIntakes && (
                  <> Gdy klient go nie ma, pin ląduje w średniej współrzędnych jego ujęć i jest
                    opisany jako pozycja przybliżona{approxPins > 0 ? ` — teraz ${approxPins}` : ''}.</>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head"><div className="card-title">Podsumowanie widoku</div></div>
            <div className="card-body text-[12px] text-ink-700 grid gap-1">
              {/* Liczby opisują to, co NARYSOWANE: wyłączona warstwa ma dawać zero, inaczej karta
                  „Podsumowanie widoku" mówi o punktach, których na mapie nie ma. */}
              <div className="flex justify-between">
                <span>Klientów na mapie:</span><strong>{showClients ? visiblePins.length : 0}</strong>
              </div>
              {canClients && (
                <div className="flex justify-between text-ink-500">
                  <span>Klientów ogółem:</span><strong>{clients.length}</strong>
                </div>
              )}
              {canIntakes && (
                <>
                  <div className="flex justify-between">
                    <span>Ujęć na mapie:</span><strong>{showIntakes ? intakesWithCoords.length : 0}</strong>
                  </div>
                  <div className="flex justify-between text-ink-500"><span>Ujęć ogółem:</span><strong>{intakes.length}</strong></div>
                  {intakesWithoutCoords > 0 && (
                    <div className="flex justify-between text-amber-700">
                      <span>Ujęć bez współrzędnych:</span><strong>{intakesWithoutCoords}</strong>
                    </div>
                  )}
                </>
              )}
              {gpsAktywne && (
                <>
                  <div className="flex justify-between border-t border-ink-100 pt-1 mt-1">
                    <span>Pojazdów z GPS:</span><strong>{gpsTotals.total}</strong>
                  </div>
                  <div className="flex justify-between"><span>W ruchu:</span><strong className="text-green-700">{gpsTotals.driving}</strong></div>
                  <div className="flex justify-between"><span>Postój z zapłonem:</span><strong className="text-amber-700">{gpsTotals.idling}</strong></div>
                  <div className="flex justify-between"><span>Postój:</span><strong className="text-ink-700">{gpsTotals.parked}</strong></div>
                  {gpsTotals.stale > 0 && (
                    <div className="flex justify-between text-ink-500">
                      <span>Sygnał nieaktualny:</span><strong>{gpsTotals.stale}</strong>
                    </div>
                  )}
                  {/* Nierozpoznane urządzenie widać na mapie, ale bez nazwy pojazdu — to zadanie
                      do odrobienia w rejestrze, więc liczba musi być widoczna, a nie ukryta. */}
                  {gpsTotals.unmatched > 0 && (
                    <div className="flex justify-between text-amber-700"
                         title="Urządzenie GPS, którego nie dopasowano do żadnego pojazdu — wskaż je w karcie pojazdu albo popraw numer rejestracyjny.">
                      <span>Bez powiązania z pojazdem:</span><strong>{gpsTotals.unmatched}</strong>
                    </div>
                  )}
                  <div className="text-[10px] text-ink-400">
                    {gpsFetchedAt
                      ? `Odczyt: ${gpsFetchedAt.toLocaleTimeString('pl-PL')}`
                      : 'Pobieranie pozycji…'}
                    {(gpsCfg?.refreshSeconds ?? 0) > 0
                      ? ` · odświeżanie co ${gpsCfg!.refreshSeconds} s`
                      : ' · bez automatycznego odświeżania'}
                  </div>
                </>
              )}
              {canWorks && showWorks && (
                <>
                  <div className="flex justify-between border-t border-ink-100 pt-1 mt-1">
                    <span>Prac w widoku:</span><strong>{totals.total}</strong>
                  </div>
                  <div className="flex justify-between"><span>W toku:</span><strong className="text-orange-600">{totals.inProgress}</strong></div>
                  <div className="flex justify-between"><span>Zlecone:</span><strong className="text-red-600">{totals.ordered}</strong></div>
                  {/* Bez tego wiersza rozbicie nie sumowało się do „Prac w widoku" — OCZEKIWANE
                      wypadało z podsumowania, choć jest w filtrach i w legendzie. */}
                  <div className="flex justify-between"><span>Oczekiwane:</span><strong className="text-amber-700">{totals.pending}</strong></div>
                  <div className="flex justify-between"><span>Zakończone:</span><strong className="text-green-700">{totals.done}</strong></div>
                  {totals.overdue > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Po terminie umowy:</span><strong>{totals.overdue}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Dawna mapa prac wypisywała każdego klienta bez własnych lat/lng — to była lista roboty
              „uzupełnij pinezkę". Po scaleniu tacy klienci bywają na mapie (pozycja ze średniej ujęć),
              więc bez tej sekcji wypadliby ze wszystkich list i nikt by ich już nie uzupełnił. */}
          {approxPins > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Klienci bez własnego punktu</div>
                <span className="ml-auto text-[11px] text-ink-400">{approxPins}</span>
              </div>
              <div className="card-body !p-0">
                <div className="max-h-[220px] overflow-y-auto">
                  <ul className="divide-y divide-ink-100">
                    {visiblePins.filter(c => c.approx).map(c => (
                      <li key={c.id} className="px-3 py-2 text-[12px] hover:bg-ink-50">
                        {canClients ? (
                          <NavLink to={`/klienci/${c.id}`} className="text-water-600 hover:underline font-semibold">
                            {c.name}
                          </NavLink>
                        ) : (
                          <span className="font-semibold text-ink-700">{c.name}</span>
                        )}
                        {c.city && <span className="text-ink-500"> · {c.city}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-3 py-2 text-[10px] text-ink-400 border-t border-ink-100">
                  Stoją w średniej współrzędnych swoich ujęć — pozycja przybliżona. Otwórz klienta →
                  Edytuj → „Współrzędne klienta", żeby wskazać miejsce, do którego naprawdę się jeździ.
                </div>
              </div>
            </div>
          )}

          {clientsOffMap.length > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Klienci bez pinezki</div>
                <span className="ml-auto text-[11px] text-ink-400">{clientsOffMap.length}</span>
              </div>
              <div className="card-body !p-0">
                <div className="max-h-[320px] overflow-y-auto">
                  <ul className="divide-y divide-ink-100">
                    {clientsOffMap.map(c => (
                      <li key={c.id} className="px-3 py-2 text-[12px] hover:bg-ink-50">
                        {canClients ? (
                          <NavLink to={`/klienci/${c.id}`} className="text-water-600 hover:underline font-semibold">
                            {c.name}
                          </NavLink>
                        ) : (
                          <span className="font-semibold text-ink-700">{c.name}</span>
                        )}
                        {c.city && <span className="text-ink-500"> · {c.city}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-3 py-2 text-[10px] text-ink-400 border-t border-ink-100">
                  Ani własnego punktu, ani geolokalizowanego ujęcia. Otwórz klienta → Edytuj →
                  „Współrzędne klienta" (punkt nie musi pokrywać się z adresem siedziby) albo dodaj
                  lat/lng do któregoś z jego ujęć.
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function intakePopup(i: Intake, canClients: boolean): string {
  return `
    <div style="font-family:system-ui;font-size:13px;">
      <div style="font-weight:600;color:#111;">${escapeHtml(i.name)}</div>
      <div style="color:#666;font-family:monospace;font-size:11px;margin-bottom:4px;">${escapeHtml(i.number)}</div>
      ${i.client?.name ? `<div style="color:#374151;">Klient: ${escapeHtml(i.client.name)}</div>` : ''}
      ${i.town ? `<div style="color:#666;">${escapeHtml(i.town)}</div>` : ''}
      <div style="margin-top:6px;">
        <a href="/intakes/${i.id}" data-nav style="color:#2563eb;text-decoration:underline;font-size:12px;">Otwórz ujęcie →</a>
        ${i.client && canClients ? `<span style="color:#d1d5db;margin:0 6px;">·</span><a href="/klienci/${i.client.id}" data-nav style="color:#2563eb;text-decoration:underline;font-size:12px;">Klient →</a>` : ''}
      </div>
    </div>`;
}

function clientPopup(
  c: ClientPin,
  opt: { showWorks: boolean; canClients: boolean; canWorks: boolean },
): string {
  const w = c.works;
  const works = opt.showWorks && w
    ? `
      <div style="margin-top:6px;color:#374151;">
        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${w.color};margin-right:5px;"></span>
        ${escapeHtml(w.dominantStatusName)}
      </div>
      <div style="color:#666;font-size:11px;margin-top:4px;">
        ${odmien(w.worksTotal, 'praca', 'prace', 'prac')} · w toku ${w.worksInProgress} · zlecone ${w.worksOrdered} · zakończone ${w.worksDone}
        ${w.worksOverdue > 0 ? `<br><strong style="color:#dc2626;">po terminie: ${w.worksOverdue}</strong>` : ''}
      </div>`
    : '';
  const brakDanychOPracach = opt.showWorks && opt.canWorks && !w
    ? '<div style="color:#6b7280;font-size:11px;margin-top:6px;">Brak danych o pracach — klient nie ma własnego punktu w rejestrze prac.</div>'
    : '';
  const intakes = c.intakesOnMap > 0
    ? `<div style="color:#666;font-size:11px;margin-top:4px;">${odmien(c.intakesOnMap, 'ujęcie', 'ujęcia', 'ujęć')} na mapie</div>`
    : '';
  // Skąd wzięła się pinezka — bez tego przybliżenie ze średniej ujęć wygląda jak pomiar.
  const origin = c.approx
    ? '<div style="color:#b45309;font-size:10px;margin-top:4px;">Pozycja przybliżona — średnia współrzędnych ujęć</div>'
    : (c.locationNote ? `<div style="color:#9ca3af;font-size:10px;margin-top:4px;">${escapeHtml(c.locationNote)}</div>` : '');
  const links = [
    opt.canClients ? `<a href="/klienci/${c.id}" data-nav style="color:#2563eb;text-decoration:underline;font-size:12px;">Otwórz klienta →</a>` : '',
    opt.canWorks ? `<a href="/prace?clientId=${c.id}" data-nav style="color:#2563eb;text-decoration:underline;font-size:12px;">Prace klienta →</a>` : '',
  ].filter(Boolean).join('<span style="color:#d1d5db;margin:0 6px;">·</span>');

  return `
    <div style="font-family:system-ui;font-size:13px;">
      <div style="font-weight:600;color:#111;">${escapeHtml(c.name)}</div>
      ${c.city ? `<div style="color:#666;font-size:11px;">${escapeHtml(c.city)}</div>` : ''}
      ${works}
      ${brakDanychOPracach}
      ${intakes}
      ${origin}
      ${links ? `<div style="margin-top:6px;">${links}</div>` : ''}
    </div>`;
}

/**
 * Popup pojazdu. Mówi trzy rzeczy, w tej kolejności: CO to jest, W JAKIM jest stanie i KIEDY był
 * ostatni pomiar. Wiek pomiaru jest tu równie ważny jak sama pozycja — punkt sprzed dwóch godzin
 * bez tej informacji wygląda dokładnie tak samo jak punkt sprzed dwóch minut.
 */
function vehiclePopup(v: GpsVehiclePosition, canFleet: boolean): string {
  const naglowek = escapeHtml(v.displayName);
  const podtytul = v.vehicleId != null && v.registrationNumber
    ? `<div style="color:#666;font-family:monospace;font-size:11px;">${escapeHtml(v.registrationNumber)}</div>`
    : '';
  const kierowca = v.driverName
    ? `<div style="color:#374151;font-size:12px;margin-top:2px;">Kierowca: ${escapeHtml(v.driverName)}</div>`
    : '';
  const predkosc = v.status === 'DRIVING' ? ` · ${v.speedKmh} km/h` : '';
  const wiek = v.ageMinutes == null
    ? 'czas pomiaru nieznany'
    : v.ageMinutes < 1 ? 'przed chwilą' : `${wiekTekst(v.ageMinutes)} temu`;
  // Urządzenie bez powiązania: mówimy wprost, co zrobić, zamiast zostawiać samą surową nazwę.
  const brakPowiazania = v.vehicleId == null
    ? `<div style="color:#b45309;font-size:10px;margin-top:4px;">
         Urządzenie nie jest powiązane z pojazdem w rejestrze${canFleet ? ' — wskaż je w karcie pojazdu (Pojazdy → Edytuj → Urządzenie GPS)' : ''}.
       </div>`
    : (v.matchSource === 'PLATE'
      ? '<div style="color:#9ca3af;font-size:10px;margin-top:4px;">Dopasowano automatycznie po numerze rejestracyjnym</div>'
      : '');
  const link = v.vehicleId != null && canFleet
    ? `<div style="margin-top:6px;"><a href="/pojazdy/${v.vehicleId}" data-nav style="color:#2563eb;text-decoration:underline;font-size:12px;">Otwórz pojazd →</a></div>`
    : '';

  return `
    <div style="font-family:system-ui;font-size:13px;">
      <div style="font-weight:600;color:#111;">${naglowek}</div>
      ${podtytul}
      ${kierowca}
      <div style="margin-top:6px;color:#374151;">
        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${v.color};margin-right:5px;"></span>
        ${escapeHtml(v.statusName)}${predkosc}
      </div>
      <div style="color:${v.stale ? '#b45309' : '#666'};font-size:11px;margin-top:4px;">Pomiar: ${wiek}</div>
      ${brakPowiazania}
      ${link}
    </div>`;
}

/** Wiek pomiaru po polsku: minuty do godziny, dalej godziny, powyżej doby — dni. */
function wiekTekst(minuty: number): string {
  if (minuty < 60) return odmien(minuty, 'minutę', 'minuty', 'minut');
  const godziny = Math.floor(minuty / 60);
  if (godziny < 24) return odmien(godziny, 'godzinę', 'godziny', 'godzin');
  return odmien(Math.floor(godziny / 24), 'dzień', 'dni', 'dni');
}

/** Polska odmiana liczebnika: 1 praca, 2-4 prace, 5+ prac (z wyjątkiem 12-14). */
function odmien(n: number, jeden: string, kilka: string, wiele: string): string {
  const reszta10 = n % 10;
  const reszta100 = n % 100;
  if (n === 1) return `${n} ${jeden}`;
  if (reszta10 >= 2 && reszta10 <= 4 && (reszta100 < 12 || reszta100 > 14)) return `${n} ${kilka}`;
  return `${n} ${wiele}`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
